import { forwardRef, useCallback, useRef, type ComponentPropsWithRef } from "react";

import { createContext } from "@radix-ui/react-context";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "..";

/* -------------------------------------------------------------------------------------------------
 * Steps
 * -----------------------------------------------------------------------------------------------*/

const STEPS_NAME = "Steps";

interface StepContextValue {
  getNextStepNumber: () => number;
}

const [StepsContextProvider, useStepsContext] = createContext<StepContextValue>(STEPS_NAME);

type StepsProps = ComponentPropsWithRef<"div">;

const Root = forwardRef<HTMLDivElement, StepsProps>(({ children, className, ...props }, forwardedRef) => {
  const currentIndexRef = useRef(1);

  const getNextStepNumber = useCallback(() => {
    const num = currentIndexRef.current;
    currentIndexRef.current += 1;
    return num;
  }, []);

  // Reset step number on each render to support SSR or re-renders
  currentIndexRef.current = 1;

  return (
    <StepsContextProvider getNextStepNumber={getNextStepNumber}>
      <div {...props} ref={forwardedRef} className={cn("gap-5 flex flex-col", className)}>
        {children}
      </div>
    </StepsContextProvider>
  );
});

Root.displayName = STEPS_NAME;

/* -------------------------------------------------------------------------------------------------
 * Step
 * -----------------------------------------------------------------------------------------------*/

const STEP_NAME = "Step";

type StepProps = ComponentPropsWithRef<"div">;

const Step = forwardRef<HTMLDivElement, StepProps>(({ className, children, ...props }, forwardedRef) => {
  const { getNextStepNumber } = useStepsContext(STEP_NAME);

  const stepNumberRef = useRef<number | null>(null);
  stepNumberRef.current ??= getNextStepNumber();

  return (
    <div
      {...props}
      ref={forwardedRef}
      className={cn("gap-2.5 gap-y-1.25 grid grid-flow-col grid-cols-[29px_auto] grid-rows-[auto_auto]", className)}
    >
      <div
        className={cn(
          "size-7.25 font-medium m-auto flex items-center justify-center rounded-[5px] bg-primary-gray text-primary-dark-blue",
          className,
        )}
      >
        {stepNumberRef.current}
      </div>
      <div className={cn("my-0 w-0.5 mx-auto h-full bg-primary-darker-gray", className)} />
      {children}
    </div>
  );
});

Step.displayName = "Step";

/* -------------------------------------------------------------------------------------------------
 * Title
 * -----------------------------------------------------------------------------------------------*/

interface TitleProps extends ComponentPropsWithRef<"h3"> {
  asChild?: boolean;
}

const Title = forwardRef<HTMLHeadingElement, TitleProps>(({ asChild, className, ...props }, forwardedRef) => {
  const Comp = asChild ? Slot : "h3";
  return (
    <Comp
      {...props}
      ref={forwardedRef}
      className={cn("m-0 font-medium self-center text-base text-primary-dark-blue", className)}
    />
  );
});

Title.displayName = "Title";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

type ContentProps = ComponentPropsWithRef<"div">;

const Content = forwardRef<HTMLDivElement, ContentProps>(({ children, ...props }, forwardedRef) => (
  <div {...props} ref={forwardedRef}>
    {children}
  </div>
));

Content.displayName = "Content";

export { Content, Root, Step, Title };
export type { ContentProps, StepProps, StepsProps, TitleProps };
